<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { storeToRefs } from 'pinia'
import BaseButtonPrimary from '../../base/BaseButtonPrimary.vue'
import { navigateToNextStep } from '../../../utilities/account/useInvestmentProcessNavigation'
import { useStockInvestmentStore } from '../../../utilities/investment-process/useStockInvestment'
import BaseInputStock from '../../base/BaseInputStock.vue'
import PreEmptiveRightsInfoBanner from './../secondaryMarket/PreEmptiveRightsInfoBanner.vue'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import { getPendingInvestmentCookie } from '../../../utilities/investment-process/usePendingInvestmentProcess'
import { getCampaignData } from '../../../utilities/investment-process/useCampaign'
import { formatCurrency, roundToPrecision } from '../../../utilities/helpers'
import { evaluate, round } from 'mathjs'
import { retrievePreEmptiveRights as apiRetrievePreEmptiveRights } from '../../../utilities/api'
import { isLegalProfile } from '../../../utilities/investment-process/useUserProfileType'

const useInvestmentStore = useStockInvestmentStore()
const investmentProcessStore = useInvestmentProcessDataState()
const { investmentAmount, incentiveUid } = storeToRefs(investmentProcessStore)
const getPendingInvestment = getPendingInvestmentCookie()
const campaignData = getCampaignData()

// -----------------------------------------------------------------------
// PRE-EMPTIVE RIGHTS STATE & VALIDATION
// -----------------------------------------------------------------------
const availablePreEmptiveRights = ref(0)
const isRightsLoading = ref(false)
// pre-emptive rights are tracked with 5 decimal places (e.g. subscription ratio 4.1)
const PRE_EMPTIVE_RIGHTS_DECIMALS = 5

const isPreEmptiveRightsRequired = computed(() => {
    return !!getCampaignData()?.asset_data?.sm_pre_emptive_rights_required
})

const subscriptionRatio = computed(() => {
    const ratio = Number(getCampaignData()?.asset_data?.sm_subscription_ratio)
    return isNaN(ratio) || ratio <= 0 ? 1 : ratio
})

const requiredPreEmptiveRights = computed(() => {
    const shares = Math.round(Number(clientInvestmentInShares.value) || 0)
    return roundToPrecision(shares * subscriptionRatio.value, PRE_EMPTIVE_RIGHTS_DECIMALS)
})

const isInvestmentInvalid = computed(() => {
    if (!clientInvestmentInAmount.value || clientInvestmentInAmount.value < min.value) {
        return true
    }

    if (isPreEmptiveRightsRequired.value) {
        if (availablePreEmptiveRights.value <= 0 || requiredPreEmptiveRights.value > availablePreEmptiveRights.value) {
            return true
        }
    }

    return false
})

function validateAndAdjustPreEmptiveRights() {
    if (!isPreEmptiveRightsRequired.value) {
        return true
    }

    if (availablePreEmptiveRights.value <= 0 || requiredPreEmptiveRights.value > availablePreEmptiveRights.value) {
        const maxAllowedShares = Math.floor(
            roundToPrecision(availablePreEmptiveRights.value / subscriptionRatio.value, PRE_EMPTIVE_RIGHTS_DECIMALS)
        )

        clientInvestmentInShares.value = maxAllowedShares
        clientInvestmentInAmount.value = round(evaluate(`${maxAllowedShares} * ${price.value}`), 2)
        investmentAmount.value = clientInvestmentInAmount.value
        return false
    }

    return true
}

async function retrievePreEmptiveRights() {
    if (!isPreEmptiveRightsRequired.value) return

    isRightsLoading.value = true
    try {
        const res = await apiRetrievePreEmptiveRights(
            isLegalProfile() ? 'legal' : 'natural',
            getCampaignData().asset_data.asset_uid
        )
        availablePreEmptiveRights.value = res?.data?.available_pre_emptive_rights ?? res?.data?.rights ?? 0
        validateAndAdjustPreEmptiveRights()
    } catch (e) {
        console.error('Failed to retrieve pre-emptive rights:', e)
        availablePreEmptiveRights.value = 0
    } finally {
        isRightsLoading.value = false
    }
}

function handleNextStep() {
    if (isInvestmentInvalid.value) {
        return
    }

    if (validateAndAdjustPreEmptiveRights()) {
        navigateToNextStep()
    }
}

watch(
    () => incentiveUid.value,
    () => {
        const incentive = campaignData.assets_incentives.find(
            (incentive) => incentive.asset_inc_uid === incentiveUid.value
        )
        if (incentive) {
            const stepAmount = evaluate(`${step.value} * ${price.value}`)
            if (incentive.incentives_amount % stepAmount !== 0) {
                clientInvestmentInAmount.value =
                    incentive.incentives_amount + stepAmount - (incentive.incentives_amount % stepAmount)
            } else {
                clientInvestmentInAmount.value = incentive.incentives_amount
            }
            setTimeout(() => {
                adjustAmount()
                adjustShares()
            }, 1000)
        }
    }
)

const { step, max, min, stockNumber, price, clientInvestmentInShares, clientInvestmentInAmount } =
    storeToRefs(useInvestmentStore)
const amountDebounce = ref(null)
const sharesDebounce = ref(null)

const hideAmount = window.emissionHideInvestmentAmountRow

const adjustShares = async () => {
    const remainder = evaluate(`${clientInvestmentInShares.value} % ${step.value}`)
    const halfStep = evaluate(`${step.value} / 2`)

    if (remainder !== 0) {
        if (remainder > halfStep) {
            clientInvestmentInShares.value = clientInvestmentInShares.value - remainder + step.value
        } else {
            clientInvestmentInShares.value -= remainder
        }
    }

    if (clientInvestmentInShares.value > stockNumber.value) {
        clientInvestmentInShares.value = stockNumber.value
    }
    if (clientInvestmentInShares.value < evaluate(`${min.value} / ${price.value}`)) {
        clientInvestmentInShares.value = evaluate(`${min.value} / ${price.value}`)
    }
    clientInvestmentInAmount.value = round(evaluate(`${clientInvestmentInShares.value} * ${price.value}`), 2)

    validateAndAdjustPreEmptiveRights()
}

const adjustAmount = () => {
    const stepAmount = evaluate(`${step.value} * ${price.value}`)
    const remainder = evaluate(`${clientInvestmentInAmount.value} % ${stepAmount}`)
    const halfStep = evaluate(`${stepAmount} / 2`)

    if (remainder !== 0) {
        if (remainder > halfStep) {
            clientInvestmentInAmount.value = round(clientInvestmentInAmount.value - remainder + stepAmount, 2)
        } else {
            clientInvestmentInAmount.value = round(clientInvestmentInAmount.value - remainder, 2)
        }
    }

    if (clientInvestmentInAmount.value > max.value) {
        clientInvestmentInAmount.value = round(max.value, 2)
    }
    if (clientInvestmentInAmount.value < min.value) {
        clientInvestmentInAmount.value = round(min.value, 2)
    }
    clientInvestmentInShares.value = round(evaluate(`${clientInvestmentInAmount.value} / ${price.value}`), 0)

    validateAndAdjustPreEmptiveRights()
}

watch(
    clientInvestmentInAmount,
    () => {
        if (amountDebounce.value) {
            clearTimeout(amountDebounce.value)
        }
        amountDebounce.value = setTimeout(() => {
            adjustAmount()
            investmentAmount.value = clientInvestmentInAmount.value
        }, 500)
    },
    { immediate: false }
)

watch(
    clientInvestmentInShares,
    () => {
        if (sharesDebounce.value) {
            clearTimeout(sharesDebounce.value)
        }
        sharesDebounce.value = setTimeout(() => {
            adjustShares()
        }, 500)
    },
    { immediate: false }
)

watch(
    () => investmentAmount.value,
    () => {
        clientInvestmentInAmount.value = investmentAmount.value
    },
    { immediate: false }
)

onMounted(() => {
    retrievePreEmptiveRights()

    const urlParams = new URLSearchParams(window.location.search)
    const iParam = urlParams.get('i')
    if (iParam) {
        const incentive = campaignData.assets_incentives.find((incentive) => incentive.asset_inc_uid === iParam)
        if (incentive) {
            incentiveUid.value = iParam
            const stepAmount = evaluate(`${step.value} * ${price.value}`)
            if (incentive.incentives_amount % stepAmount !== 0) {
                clientInvestmentInAmount.value =
                    incentive.incentives_amount + stepAmount - (incentive.incentives_amount % stepAmount)
            } else {
                clientInvestmentInAmount.value = incentive.incentives_amount
            }
            setTimeout(() => {
                adjustAmount()
                adjustShares()
            }, 1000)
        }
    } else {
        if (getPendingInvestment.investment_amount && getPendingInvestment.investment_amount > 0) {
            clientInvestmentInAmount.value = getPendingInvestment.investment_amount
            clientInvestmentInShares.value = round(
                evaluate(`${getPendingInvestment.investment_amount} / ${price.value}`),
                0
            )
        } else {
            clientInvestmentInAmount.value = min.value
            clientInvestmentInShares.value = round(evaluate(`${min.value} / ${price.value}`), 0)
        }
    }
})
</script>

<template>
    <div>
        <PreEmptiveRightsInfoBanner
            :is-required="isPreEmptiveRightsRequired"
            :is-loading="isRightsLoading"
            :available-rights="availablePreEmptiveRights"
            :subscription-ratio="subscriptionRatio"
            :shares="clientInvestmentInShares" />

        <div>
            <p class="pb-3 text-base font-bold text-gray-700">
                {{ $t('investment_process.stock_investment_process_desired_shares') }}
            </p>
            <div class="grid grid-cols-1 items-center md:grid-cols-4 md:space-x-4">
                <BaseInputStock
                    v-model="clientInvestmentInShares"
                    name="stock_invest"
                    size="xl"
                    type="shares"
                    @blur="adjustShares" />
                <div class="my-4 space-y-0.5 text-gray-700 md:text-xs">
                    <p class="text-left">
                        <strong>{{ $t('investment_process.minimum_investment') }}</strong
                        >:
                        {{ round(evaluate(`${min} / ${price}`), 0) }}
                    </p>
                    <p class="text-left">
                        <strong>{{ $t('investment_process.step_level') }}</strong
                        >:
                        {{ step }}
                    </p>
                    <p class="text-left">
                        <strong>{{ $t('investment_process.stock_investment_price') }}</strong
                        >:
                        {{ price }}
                    </p>
                </div>
                <div class="flex items-center justify-center">
                    <BaseButtonPrimary
                        class="w-full md:w-auto"
                        :disabled="isInvestmentInvalid"
                        @click="handleNextStep()"
                        >{{ $t('investment_process.invest') }}
                    </BaseButtonPrimary>
                </div>
            </div>
        </div>

        <div v-if="!hideAmount">
            <p class="mt-4 pb-3 text-base font-bold text-gray-700">
                {{ $t('investment_process.or_select_amount') }}
            </p>
            <div class="grid grid-cols-1 items-center md:grid-cols-4 md:space-x-4">
                <BaseInputStock
                    v-model="clientInvestmentInAmount"
                    name="amout_invest"
                    size="xl"
                    type="amount"
                    @blur="adjustAmount" />
                <div class="my-4 space-y-0.5 text-gray-700 md:text-xs">
                    <p class="text-left">
                        <strong>{{ $t('investment_process.minimum_investment') }}</strong
                        >:
                        {{ min }}
                    </p>
                    <p class="text-left">
                        <strong>{{ $t('investment_process.step_level') }}</strong
                        >:
                        {{ step * price }}
                    </p>
                    <p class="text-left">
                        <strong>{{ $t('investment_process.stock_investment_price') }}</strong
                        >:
                        {{ price }}
                    </p>
                </div>
                <div class="flex items-center justify-center">
                    <BaseButtonPrimary
                        class="w-full md:w-auto"
                        :disabled="isInvestmentInvalid"
                        @click="handleNextStep()"
                        >{{ $t('investment_process.invest') }}
                    </BaseButtonPrimary>
                </div>
            </div>
        </div>

        <div v-if="campaignData.asset_data.show_amount_slider" class="py-4">
            <input
                v-model="clientInvestmentInShares"
                :step="step"
                type="range"
                class="range accent-primary"
                :min="evaluate(`${min} / ${price}`)"
                :max="stockNumber" />
            <div></div>
            <div class="flex justify-between text-sm font-semibold text-gray-700">
                <p>{{ round(evaluate(`${min} / ${price}`), 0) }}</p>
                <p>{{ stockNumber }}</p>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
